import * as React from 'react'
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  Divider,
  Link
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import influencerRoutes from '../../influencerRoutes'
import useStyles from './styles'
import BrandLogo from './BrandLogo'
import { useLocation } from 'react-router-dom'
import { useTranslation, withTranslation } from 'react-i18next'
import { influencerSidebarIcons } from './sidebarIcons'
import {
  isAdminViewingInfluencer,
  getViewingInfluencerName,
  exitInfluencer
} from '../../utils/influencerContext'

const drawerWidth = 260

function InfluencerSidebar(props) {
  const { t } = useTranslation()
  const location = useLocation()
  const classes = useStyles()
  const { window } = props
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const user = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku'))
    : null
  const adminViewing = isAdminViewingInfluencer()
  // Whose dashboard this is: the impersonated influencer, or the logged-in one
  const displayName = adminViewing ? getViewingInfluencerName() : user?.name

  // Profile and Settings edit the caller's own account, so they are pointless
  // (and would fail) while an admin is looking at someone else's dashboard
  const visibleRoutes = adminViewing
    ? influencerRoutes.filter(
        route => !['/profile', '/settings'].includes(route.path)
      )
    : influencerRoutes
  const backToAdmin = () => {
    exitInfluencer()
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const createLinks = (
    <Box className={classes.sidebarBox}>
      <Toolbar className={[classes.rowDisplay, classes.logo]}>
        <BrandLogo />
      </Toolbar>
      <Divider className={classes.sidebarDivider} />
      {displayName ? (
        <Typography className={classes.headingText} variant="h3">
          {displayName}
        </Typography>
      ) : null}
      <Box className={classes.sidebarList}>
        {visibleRoutes.map((prop, key) => {
          const to = `${prop.layout}${prop.path}`
          const isActive = location.pathname === to
          const Icon = influencerSidebarIcons[prop.name]
          return (
            <Link
              key={key}
              className={[
                classes.rowDisplay,
                classes.sidebarLink,
                isActive && classes.active
              ]}
              href={'#' + to}
              data-sidebar-active={isActive ? 'true' : undefined}
              onClick={() => setMobileOpen(false)}
              underline="none">
              <Icon
                sx={{
                  fontSize: 20,
                  color: isActive ? 'common.white' : '#1F2937'
                }}
              />
              <Typography
                variant="h6"
                className={[
                  classes.linkText,
                  isActive ? classes.whiteText : classes.blackText
                ]}>
                {t(prop.name)}
              </Typography>
            </Link>
          )
        })}
        {adminViewing ? (
          // An anchor, not component="button": the sidebar styles assume a
          // block-level link, and a button shrinks to fit and picks up the
          // browser's own chrome, which broke the hover pill.
          <Link
            className={[classes.rowDisplay, classes.sidebarLink]}
            href="#/super_admin/influencers"
            onClick={backToAdmin}
            underline="none">
            {React.createElement(influencerSidebarIcons['Back to Admin'], {
              sx: { fontSize: 20, color: '#1F2937' }
            })}
            <Typography
              variant="h6"
              className={[classes.linkText, classes.blackText]}>
              {t('BackToAdmin')}
            </Typography>
          </Link>
        ) : null}
      </Box>
    </Box>
  )

  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <>
      <AppBar
        mb={10}
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' },
          bgcolor: 'common.white',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          marginBottom: '100px'
        }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '78%',
              backgroundColor: 'common.white'
            }
          }}>
          {createLinks}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'common.white',
              borderRight: '1px solid',
              borderColor: 'divider'
            }
          }}
          open>
          {createLinks}
        </Drawer>
      </Box>
    </>
  )
}

export default withTranslation()(InfluencerSidebar)
